import React, { useEffect, useState, Component } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, User, ChevronDown, Briefcase, Calendar, Users as UsersIcon, Phone, Mail, ShoppingCart, ArrowRight, MapPin, Clock } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const {
    cartItemCount
  } = useCart();
  const isStorePage = location.pathname === '/store';
  const isCartPage = location.pathname === '/cart';
  const showCartIcon = isStorePage || isCartPage;
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close search modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (isSearchOpen && !target.closest('.search-dropdown') && !target.closest('.search-button')) {
        setIsSearchOpen(false);
      }
    };
    if (isSearchOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('click', handleClickOutside, true);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isSearchOpen]);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to events page with search query
      navigate(`/events?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };
  const popularSearches = [{
    term: 'Marathon',
    icon: MapPin
  }, {
    term: '5K races',
    icon: MapPin
  }, {
    term: 'Trail running',
    icon: MapPin
  }, {
    term: 'Half marathon',
    icon: MapPin
  }];
  const quickLinks = [{
    name: 'Browse All Events',
    path: '/events'
  }, {
    name: 'Find Running Clubs',
    path: '/clubs'
  }, {
    name: 'Travel Deals',
    path: '/travel-deals'
  }, {
    name: 'Pace Teams',
    path: '/pace-teams'
  }];
  const isActive = (path: string) => location.pathname === path;
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Events',
    path: '/events'
  }, {
    name: 'Clubs',
    path: '/clubs'
  }, {
    name: 'Travel Deals',
    path: '/travel-deals'
  }, {
    name: 'Blog',
    path: '/blog'
  }, {
    name: 'Store',
    path: '/store'
  }];
  const utilityLinks = [{
    name: 'Business Profile',
    path: '/sign-in',
    icon: Briefcase
  }, {
    name: 'List Events',
    path: '/sign-in',
    icon: Calendar
  }, {
    name: 'List Clubs',
    path: '/sign-in',
    icon: UsersIcon
  }, {
    name: 'Contact Us',
    path: '/contact',
    icon: Phone
  }];
  return <>
      <header className={`w-full sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`} data-id="element-141">
        {/* Utility Bar */}
        <div className="bg-gradient-to-r from-[#0E578E] to-[#0a4069] text-white" data-id="element-142">
          <div className="max-w-7xl mx-auto px-10" data-id="element-143">
            <div className="flex justify-end items-center h-10" data-id="element-144">
              <div className="hidden md:flex items-center gap-1" data-id="element-145">
                {utilityLinks.map(link => <Link key={link.name} to={link.path} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white/90 hover:text-white hover:bg-white/10 rounded-md transition-all" data-id="element-146">
                    <link.icon className="h-3.5 w-3.5" data-id="element-147" />
                    {link.name}
                  </Link>)}
              </div>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className={`transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-lg' : 'bg-white'}`} data-id="element-148">
          <div className="max-w-7xl mx-auto px-10" data-id="element-149">
            <div className="flex justify-between items-center h-20" data-id="element-150">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3 group" data-id="element-151">
                <img src="https://cdn.magicpatterns.com/uploads/37LqrxU35PwjhXWE2X2bUm/logo_(1)_(1).jpg" alt="RaceLookup Logo" className="h-12 w-auto object-contain rounded-xl" data-id="element-152" />
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-2" data-id="element-153">
                {navLinks.map(link => <Link key={link.name} to={link.path} className={`px-4 py-2.5 text-sm font-bold rounded-xl transition-all ${isActive(link.path) ? 'text-white bg-gradient-to-r from-[#01AAE7] to-[#0199d6] shadow-md' : 'text-gray-700 hover:text-[#01AAE7] hover:bg-gray-50'}`} data-id="element-154">
                    {link.name}
                  </Link>)}
              </nav>

              {/* Right Actions */}
              <div className="hidden lg:flex items-center gap-3" data-id="element-155">
                <button onClick={() => setIsSearchOpen(!isSearchOpen)} className="search-button p-2.5 text-gray-600 hover:text-[#01AAE7] hover:bg-gray-50 rounded-xl transition-all" data-id="element-156">
                  <Search className="h-5 w-5" data-id="element-157" />
                </button>

                {/* Cart Icon - Visible on Store and Cart pages */}
                {showCartIcon && <button onClick={() => navigate('/cart')} className="relative p-2.5 text-gray-600 hover:text-[#01AAE7] hover:bg-gray-50 rounded-xl transition-all" data-id="element-158">
                    <ShoppingCart className="h-5 w-5" data-id="element-159" />
                    {cartItemCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-id="element-160">
                        {cartItemCount}
                      </span>}
                  </button>}

                <Link to="/signin" className="px-5 py-2.5 text-sm font-bold text-gray-700 hover:text-[#01AAE7] hover:bg-gray-50 rounded-xl transition-all" data-id="element-161">
                  Sign In
                </Link>

                <Link to="/create-account" className="px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-[#01AAE7] to-[#0199d6] rounded-xl hover:from-[#0199d6] hover:to-[#0188c5] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-162">
                  Get Started
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden p-2.5 text-gray-700 hover:text-[#01AAE7] hover:bg-gray-50 rounded-xl transition-all" data-id="element-163">
                {isMenuOpen ? <X className="h-6 w-6" data-id="element-164" /> : <Menu className="h-6 w-6" data-id="element-165" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && <div className="lg:hidden border-t border-gray-100 bg-white shadow-2xl" data-id="element-166">
            <div className="max-w-7xl mx-auto px-4 py-6" data-id="element-167">
              {/* Search Button Mobile */}
              <div className="mb-6" data-id="element-168">
                <button onClick={() => {
              setIsSearchOpen(true);
              setIsMenuOpen(false);
            }} className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all" data-id="element-169">
                  <Search className="h-5 w-5 text-gray-700" data-id="element-170" />
                  <span className="text-base font-bold text-gray-700" data-id="element-171">
                    Search Events
                  </span>
                </button>
              </div>

              {/* Utility Links Mobile */}
              <div className="mb-6 pb-6 border-b border-gray-100" data-id="element-172">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-173">
                  Quick Links
                </h4>
                <div className="grid grid-cols-2 gap-2" data-id="element-174">
                  {utilityLinks.map(link => <Link key={link.name} to={link.path} className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:text-[#01AAE7] hover:bg-gray-50 rounded-lg transition-all" onClick={() => setIsMenuOpen(false)} data-id="element-175">
                      <link.icon className="h-4 w-4" data-id="element-176" />
                      {link.name}
                    </Link>)}
                </div>
              </div>

              {/* Main Nav Mobile */}
              <div className="space-y-2 mb-6" data-id="element-177">
                {navLinks.map(link => <Link key={link.name} to={link.path} className={`block px-4 py-3 text-base font-bold rounded-xl transition-all ${isActive(link.path) ? 'text-white bg-gradient-to-r from-[#01AAE7] to-[#0199d6] shadow-md' : 'text-gray-700 hover:text-[#01AAE7] hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-178">
                    {link.name}
                  </Link>)}
              </div>

              {/* Cart Button Mobile - Only on Store and Cart pages */}
              {showCartIcon && <div className="mb-6 pb-6 border-b border-gray-100" data-id="element-179">
                  <button onClick={() => {
              navigate('/cart');
              setIsMenuOpen(false);
            }} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all" data-id="element-180">
                    <div className="flex items-center gap-3" data-id="element-181">
                      <ShoppingCart className="h-5 w-5 text-gray-700" data-id="element-182" />
                      <span className="text-base font-bold text-gray-700" data-id="element-183">
                        Shopping Cart
                      </span>
                    </div>
                    {cartItemCount > 0 && <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center" data-id="element-184">
                        {cartItemCount}
                      </span>}
                  </button>
                </div>}

              {/* Auth Buttons Mobile */}
              <div className="space-y-3 pt-6 border-t border-gray-100" data-id="element-185">
                <Link to="/signin" className="block w-full text-center px-4 py-3.5 text-base font-bold text-gray-700 border-2 border-gray-200 rounded-xl hover:border-[#01AAE7] hover:text-[#01AAE7] transition-all" onClick={() => setIsMenuOpen(false)} data-id="element-186">
                  Sign In
                </Link>
                <Link to="/create-account" className="block w-full text-center px-4 py-3.5 text-base font-bold text-white bg-gradient-to-r from-[#01AAE7] to-[#0199d6] rounded-xl hover:from-[#0199d6] hover:to-[#0188c5] transition-all shadow-lg" onClick={() => setIsMenuOpen(false)} data-id="element-187">
                  Get Started
                </Link>
              </div>
            </div>
          </div>}
      </header>

      {/* Search Dropdown */}
      {isSearchOpen && <>
          {/* Backdrop overlay */}
          <div className="fixed inset-0 z-[35] bg-black/20 cursor-pointer" onClick={e => {
        e.stopPropagation();
        setIsSearchOpen(false);
      }} data-id="element-188"></div>

          <div className="fixed top-[130px] left-0 right-0 z-[40] search-dropdown pointer-events-none" data-id="element-189">
            <div className="max-w-7xl mx-auto px-10" data-id="element-190">
              <div className="ml-auto w-1/2 bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden pointer-events-auto" data-id="element-191">
                {/* Search Input */}
                <form onSubmit={handleSearch} className="p-5 bg-gradient-to-br from-blue-50/50 to-white border-b border-gray-100" data-id="element-192">
                  <div className="relative" data-id="element-193">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2" data-id="element-194">
                      <Search className="h-5 w-5 text-[#01AAE7]" data-id="element-195" />
                    </div>
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search for races, locations, or distances..." className="w-full pl-12 pr-4 py-3.5 text-sm font-medium bg-white border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-[#01AAE7] focus:ring-2 focus:ring-[#01AAE7]/20 transition-all placeholder:text-gray-400" autoFocus data-id="element-196" />
                  </div>
                </form>

                {/* Popular Searches */}
                <div className="p-5" data-id="element-197">
                  <div className="flex items-center gap-2 mb-4" data-id="element-198">
                    <div className="w-1.5 h-5 bg-gradient-to-b from-[#01AAE7] to-[#0199d6] rounded-full" data-id="element-199"></div>
                    <h3 className="text-xs font-black text-gray-900 uppercase tracking-wider" data-id="element-200">
                      Popular Searches
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5" data-id="element-201">
                    {popularSearches.map((item, index) => <button key={item.term} onClick={() => {
                  setSearchQuery(item.term);
                  navigate(`/events?search=${encodeURIComponent(item.term)}`);
                  setIsSearchOpen(false);
                  setSearchQuery('');
                }} className="flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-gray-50 to-white hover:from-[#01AAE7]/5 hover:to-[#01AAE7]/10 border border-gray-200 hover:border-[#01AAE7] rounded-xl transition-all text-left group shadow-sm hover:shadow-md" style={{
                  animationDelay: `${index * 50}ms`
                }} data-id="element-202">
                        <div className="w-9 h-9 bg-white group-hover:bg-[#01AAE7] border border-gray-200 group-hover:border-[#01AAE7] rounded-lg flex items-center justify-center transition-all shadow-sm" data-id="element-203">
                          <item.icon className="h-4 w-4 text-gray-600 group-hover:text-white transition-colors" data-id="element-204" />
                        </div>
                        <span className="text-sm font-bold text-gray-700 group-hover:text-gray-900" data-id="element-205">
                          {item.term}
                        </span>
                      </button>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}
    </>;
}